/**
 * Packaging Material Emission Factors Grid
 * List view for packaging material emission factors — story 36910.
 * Structural base: packaging-pcf/processing-activities/processing-grid.
 */

import { FC, useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router'
import { Input, Breadcrumbs, Button } from '@mondra/ui-components'
import { useQuery } from '@apollo/client'
import { GET_ALL_MATERIAL_EMISSION_FACTORS } from '../../../../query/material-emission-factors'
import { EXPORT_MATERIAL_EMISSION_FACTORS_QUERY } from '../../../../query/export-material-emission-factors'
import { filterEnums, getTableData } from '../../../../shared/gridFunctions'
import Table from '../../../../components/Table'
import { ExportGraphqlButton } from '../../../../components/ExportGraphqlButton'
import { EXPORT_MAX_ROW_COUNT, ROUTES } from '../../../../const'
import { IMaterialEmissionFactorGridItem } from './types'
import { getMaterialEmissionFactorVars } from './helpers'

// @ts-ignore - SCSS module import
import './index.scss'

const MaterialEmissionFactorGrid: FC = () => {
  const navigate = useNavigate()

  const [tableData, setTableData] = useState<IMaterialEmissionFactorGridItem[]>([])
  const [columnDefs, setColumn] = useState([])
  const [tableSearch, setTableSearch] = useState<string | null>(null)
  const [debouncedTableSearch, setDebouncedTableSearch] = useState(tableSearch)
  const [totalCount, setTotalCount] = useState<string>('')
  const [endCursor, setEndCursor] = useState<string | null>(null)
  const [startCursor, setStartCursor] = useState<string | null>(null)
  const [emissionFactorVars, setEmissionFactorVars] = useState<any>(getMaterialEmissionFactorVars)
  const [order, setOrder] = useState([{ id: 'ASC' }])

  const { data, loading, refetch } = useQuery(GET_ALL_MATERIAL_EMISSION_FACTORS, {
    variables: {
      str: '',
      first: 100,
      last: null,
      order: order,
      ...getMaterialEmissionFactorVars,
    },
  })

  const anyRefetch = useCallback(
    (variables?: any): Promise<any> => {
      return refetch(variables)
    },
    [refetch]
  )

  const setCursors = useCallback(() => {
    if (data?.packagingEmissionFactors?.pageInfo) {
      setEndCursor(data.packagingEmissionFactors.pageInfo.endCursor)
      setStartCursor(data.packagingEmissionFactors.pageInfo.startCursor)
    }
  }, [data])

  useEffect(() => {
    const timer = setTimeout(() => setTableSearch(debouncedTableSearch), 1000)
    return () => clearTimeout(timer)
  }, [debouncedTableSearch])

  useEffect(() => {
    // Refetch on mount/data-change so navigating back to the grid shows fresh data instead of a
    // stale Apollo cache-first result (repo convention, see CLAUDE.md "Grid remount refetch").
    refetch()
    getTableData(
      data,
      loading,
      setTableData,
      setColumn,
      setTotalCount,
      setCursors,
      ROUTES.materialEmissionFactorsItem,
      'packagingEmissionFactors',
      'emissionFactor'
    )
  }, [data, loading, refetch, setCursors])

  useEffect(() => {
    if (tableSearch != null || !order[0].id) {
      if (!loading) {
        setEmissionFactorVars({
          ...filterEnums(tableSearch, getMaterialEmissionFactorVars, data),
        })
        anyRefetch({
          str: tableSearch || '',
          ...filterEnums(tableSearch, getMaterialEmissionFactorVars, data),
          first: 100,
          after: null,
          before: null,
          last: null,
          order: order,
        })
      }
    }
  }, [tableSearch, order, loading, data, anyRefetch])

  // operationName must equal the root query field name exactly — the export endpoint resolves it
  // against the schema's Query type and 500s otherwise (bugs 37321, 39141). where: null because
  // export is a full dump; the root field is already server-side type-scoped.
  const exportPayload = useMemo(
    () => ({
      operationName: 'PackagingEmissionFactors',
      query: EXPORT_MATERIAL_EMISSION_FACTORS_QUERY.loc?.source.body,
      variables: {
        first: EXPORT_MAX_ROW_COUNT,
        after: null,
        where: null,
        order,
      },
    }),
    [order]
  )

  const handleAddNew = useCallback(() => {
    navigate(`${ROUTES.materialEmissionFactorsItem}?itemId=new`)
  }, [navigate])

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDebouncedTableSearch(e.target.value)
  }, [])

  return (
    <div className={'packaging'}>
      <div style={{ maxWidth: '50rem' }} className={'mb-4'}>
        <p className={'mt-3 decoration-gray-600'}>
          <Breadcrumbs
            routes={[
              {
                path: ROUTES.materialEmissionFactors,
                title: 'Material Emission Factors Table',
              },
            ]}
          />
        </p>
        <h2 className={'mb-5 mt-3 text-2xl'}>Material Emission Factors</h2>
        <div className="packaging-actions items-center">
          <Input onChange={handleSearchChange} iconType="search" placeholder="Search" />
          <Button variant="primary" onClick={handleAddNew}>
            Add Emission Factor
          </Button>
          <ExportGraphqlButton payload={exportPayload} />
        </div>
      </div>
      <Table
        loading={loading}
        tableData={tableData}
        columnDefs={columnDefs}
        totalNumber={totalCount}
        endCursor={endCursor}
        startCursor={startCursor}
        refetch={refetch}
        defaultVars={emissionFactorVars || getMaterialEmissionFactorVars}
        setCursors={setCursors}
        tableSearch={tableSearch}
        setOrder={setOrder}
        order={order}
        suppressRowClickSelection
      />
    </div>
  )
}

export default MaterialEmissionFactorGrid
